import Parser from "@helpers/parser";

import { useRef, useState, useEffect } from "react";

import { LineChart, BarChart } from "@mui/x-charts";

import { ResponsiveFunnel } from "@nivo/funnel";

const Chart = {
  Line: ({ data, labels, type, height = 180 }) => {
    const gradientId = "line-gradient";

    function valueFormatter(v) {
      if (type === "money") {
        return `R$${Parser.price.brl(Math.floor(v * 100))}`;
      } else return v;
    }

    const values = type === "money" ? data.map((v) => v / 100) : data;

    return (
      <LineChart
        skipAnimation={false}
        height={height}
        series={[
          {
            data: values,
            curve: "linear",
            color: `url(#${gradientId})`,
            area: false,
            showMark: false,
            valueFormatter,
            animation: {
              duration: 2000, // ✅ animação de 1 segundo
              easing: "ease-out", // ✅ suavização na saída
            },
          },
        ]}
        xAxis={[
          {
            data: labels,
            scaleType: "band",
            label: "Mês", // ✅ Nome do eixo X
            tickLabelStyle: {
              fill: "#D9D9FF25",
            },
            labelStyle: {
              fill: "#D9D9FF25",
              fontSize: 12,
              fontWeight: 500,
            },
            axisLine: { stroke: "#D9D9FF25" },
          },
        ]}
        yAxis={[
          {
            labelStyle: {
              fill: "#D9D9FF25",
              fontSize: 12,
              fontWeight: 500,
            },
            axisLine: { stroke: "#D9D9FF25" },
            valueFormatter: (value) =>
              `${type === "money"
                ? "R$" + Parser.number(value)
                : Parser.number(value)
              }`,
          },
        ]}
        grid={{
          vertical: true,
          horizontal: true,
          stroke: "#D9D9FF02",
          strokeDasharray: "4 4",
        }}
        tooltip={{ trigger: "axis" }}
        margin={{
          top: 10,
          bottom: 20,
          left: type === "money" ? 70 : 40,
          right: type === "money" ? 30 : 10,
        }} // ✅ Espaço para os labels
        sx={{
          strokeWidth: 0,
          ".MuiLineElement-root": {
            strokeWidth: 0.63,
            filter: "drop-shadow(0px 0px 8px #C134F2)",
          },
          ".MuiChartsAxis-root .MuiChartsAxis-line, .MuiChartsAxis-root .MuiChartsAxis-tickLabel":
          {
            stroke: "#D9D9FF25",
            fill: "#D9D9FF25",
          },
          ".MuiChartsLegend-root": {
            display: "none",
          },
        }}
        slotProps={{
          svg: { style: { overflow: "visible" } },
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#7134F2" />
            <stop offset="100%" stopColor="#C134F2" />
          </linearGradient>
        </defs>
      </LineChart>
    );
  },

  Bar: ({ data, labels, type, height = 180 }) => {
    const gradientId = "bar-gradient";
    const clipPathId = "bar-top-round";

    function valueFormatter(v) {
      if (type === "money") {
        return `R$${Parser.price.brl(Math.floor(v * 100))}`;
      } else return v;
    }

    const values = type === "money" ? data.map((v) => v / 100) : data;

    return (
      <BarChart
        skipAnimation={false}
        height={height}
        series={[
          {
            data: values,
            color: `url(#${gradientId})`,
            valueFormatter,
            animation: {
              duration: 2000, // ✅ animação de 2 segundos
              easing: "ease-out", // ✅ suavização na saída
            },
          },
        ]}
        xAxis={[
          {
            data: labels,
            scaleType: "band",
            label: "Mês", // ✅ Nome do eixo X
            tickLabelStyle: {
              fill: "#D9D9FF25",
            },
            labelStyle: {
              fill: "#D9D9FF25",
              fontSize: 12,
              fontWeight: 500,
            },
            axisLine: { stroke: "#D9D9FF25" },
          },
        ]}
        yAxis={[
          {
            labelStyle: {
              fill: "#D9D9FF25",
              fontSize: 12,
              fontWeight: 500,
            },
            axisLine: { stroke: "#D9D9FF25" },
            valueFormatter: (value) =>
              `${type === "money"
                ? "R$" + Parser.number(value)
                : Parser.number(value)
              }`,
          },
        ]}
        grid={{
          vertical: true,
          horizontal: true,
          stroke: "#D9D9FF02",
          strokeDasharray: "4 4",
        }}
        tooltip={{ trigger: "axis" }}
        margin={{
          top: 10,
          bottom: 20,
          left: type === "money" ? 70 : 40,
          right: type === "money" ? 30 : 10,
        }} // ✅ Espaço para os labels
        sx={{
          strokeWidth: 0,
          ".MuiBarElement-root": {
            strokeWidth: 0,
            filter: "drop-shadow(0px 0px 8px rgba(195, 52, 242, 0.61))",
            clipPath: `url(#${clipPathId})`,
          },
          ".MuiChartsAxis-root .MuiChartsAxis-line, .MuiChartsAxis-root .MuiChartsAxis-tickLabel":
          {
            stroke: "#D9D9FF25",
            fill: "#D9D9FF25",
          },
          ".MuiChartsLegend-root": {
            display: "none",
          },
        }}
        slotProps={{
          svg: { style: { overflow: "visible" } },
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#7134F2" />
            <stop offset="100%" stopColor="#C134F2" />
          </linearGradient>
          <clipPath id={clipPathId} clipPathUnits="objectBoundingBox">
            {/** Rounded only top-left and top-right using quadratic curves; increased radius */}
            <path d="M0,0.22 Q0,0 0.14,0 H0.86 Q1,0 1,0.22 V1 H0 V0.22 Z" />
          </clipPath>
        </defs>
      </BarChart>
    );
  },

  Funnel: ({ data }) => {
    const margin = { top: 20, right: 20, bottom: 20, left: 20 };

    return (
      <div style={{ position: "relative", width: "100%", height: 210 }}>
        <FunnelLabels data={data} />
        <ResponsiveFunnel
          spacing={2}
          shapeBlending={0.44}
          direction="horizontal"
          data={data}
          height={180}
          enableLabel={true}
          tooltip={({ id, value, label, color }) => (
            <div
              style={{
                padding: 6,
                color: "#fff",
                background: color,
                borderRadius: 2,
                fontSize: 12,
              }}
            >
              <strong>{id}</strong>: {value}
            </div>
          )}
          labelColor={{ from: "color", modifiers: [["darker", 5]] }}
          margin={margin}
          borderWidth={20}
          colors={({ active }) => (active ? "#C134F2" : "#ffffff")}
          separatorOffset={0} // desativa separator nativo
          separatorThickness={0}
          layers={["parts", "labels", "legends"]}
        />
        <CustomSeparators data={data} margin={margin} />
      </div>
    );
  },
};

export default Chart;

const FunnelLabels = ({ data }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      margin: "0 20px",
      paddingBottom: 10,
    }}
  >
    {data.map((item) => (
      <div
        key={item.label}
        style={{
          flex: 1,
          textAlign: "center",
          color: "white",
          fontWeight: 500,
          fontSize: 14,
        }}
      >
        {item.label}
      </div>
    ))}
  </div>
);
const CustomSeparators = ({ data, margin }) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Hook para detectar resize do container
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const count = data.length;
  const widthAvailable = dimensions.width - margin.left - margin.right;
  const heightAvailable = dimensions.height - margin.top - margin.bottom;

  // Calcula posições dos separadores horizontalmente
  const separatorPositions = [];
  for (let i = 1; i < count; i++) {
    const posX = margin.left + (widthAvailable / count) * i;
    separatorPositions.push(posX);
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      <svg width={dimensions.width} height={dimensions.height}>
        {separatorPositions.map((posX, idx) => (
          <line
            key={idx}
            x1={posX}
            y1={margin.top}
            x2={posX}
            y2={dimensions.height - margin.bottom}
            stroke="#c4c4c420"
            strokeWidth={2}
            strokeDasharray="4 4"
          />
        ))}
      </svg>
    </div>
  );
};
